import React, {createContext, use, useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import {createContextualCan} from "@casl/react";
import {LinearProgress} from "@mui/material";
import {useUser} from "../components/user/Context/UserProvider";
import Loader from "../components/Loader";
import {filterForActivityGrants} from "../resources/filters";
import {getIncidentSubject, isLeadUser, isLeadUserOrHasNone} from "../resources/incidents";
import {
  check,
  createUserAbility,
  createAuthorized,
  createPermission,
  createTestAbility,
} from "../resources/authorization";
import {every, filter, isEmpty, some} from "underscore";

const AuthzContext = createContext(undefined);

export const Can = createContextualCan(AuthzContext.Consumer);

export const MemoryAuthzProvider = ({children}) => {
  if (process?.env?.NODE_ENV?.toLowerCase() !== "test") {
    return <AuthzProvider>{children}</AuthzProvider>;
  }

  const ability = createTestAbility();
  return <AuthzContext value={ability}>{children}</AuthzContext>;
};

MemoryAuthzProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthzProvider = ({children}) => {
  const {user} = useUser();
  const isAuthorizationDataReady = !isEmpty(user);

  const ability = useMemo(() => {
    if (!isAuthorizationDataReady) {
      return;
    }

    return createUserAbility(user);
  }, [isAuthorizationDataReady, user]);

  if (!isAuthorizationDataReady) {
    return (
      <>
        <LinearProgress variant="determinate" value={50} />
        <Loader fullViewingHeight />
      </>
    );
  }

  return <AuthzContext value={ability}>{children}</AuthzContext>;
};

AuthzProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuthz = () => {
  const context = use(AuthzContext);
  if (!context) {
    throw new Error("Context must be used within the correct provider");
  }
  return context;
};

export const usePermissions = () => {
  const {user} = useUser();
  const ability = useAuthz();
  const permission = createPermission;
  const authorized = useMemo(() => createAuthorized(ability), [ability]);

  const authorizedAll = useCallback((permissions) => every(permissions, authorized), [authorized]);
  const authorizedAny = useCallback((permissions) => some(permissions, authorized), [authorized]);

  const canAccessExercise = authorized(
    permission({
      perm: check("access", "Exercise"),
    }),
  );

  const canAccessIncident = authorized(
    permission({
      perm: check("access", "Incident"),
    }),
  );

  const canAccessExerciseAndIncident = canAccessExercise && canAccessIncident;
  const canAccessExerciseOrIncident = canAccessExercise || canAccessIncident;

  const canAccessMessage = authorized(
    permission({
      perm: check("access", "Message"),
    }),
  );

  const canAccessAIUtils = authorized(
    permission({
      perm: check("access", "RexAIUtilities"),
    }),
  );

  const canAccessDocumentLibrary = authorized(
    permission({
      perm: check("access", "DocumentLibrary"),
    }),
  );

  const canAccessTrustedAdvisor = authorized(
    permission({
      perm: check("access", "TrustedAdvisor"),
    }),
  );

  const canShareTrustedAdvisor = authorized(
    permission({
      perm: check("share", "TrustedAdvisor"),
    }),
  );

  const canAddScenarioPhaseActionsExercise = authorized(
    permission({
      perm: check("addScenarioPhaseActions", "Exercise"),
    }),
  );

  const canManageApiKey = authorized(
    permission({
      perm: check("manage", "ApiKey"),
    }),
  );

  const canManageAuditSummary = authorized(
    permission({
      perm: check("manage", "AuditSummary"),
    }),
  );

  const canManageDataOption = authorized(
    permission({
      perm: check("manage", "DataOption"),
    }),
  );

  const canManageDataset = authorized(
    permission({
      perm: check("manage", "Dataset"),
    }),
  );

  const canManageDocumentLibrary = authorized(
    permission({
      perm: check("manage", "DocumentLibrary"),
    }),
  );

  const canManageIntegration = authorized(
    permission({
      perm: check("manage", "Integration"),
    }),
  );

  const canManageIncidentMeetingAssets = authorized(
    permission({
      perm: check("manageMeetingAssets", "Incident"),
    }),
  );

  const canManageExerciseMeetingAssets = authorized(
    permission({
      perm: check("manageMeetingAssets", "Exercise"),
    }),
  );

  const canManageNotification = authorized(
    permission({
      perm: check("manage", "Notification"),
    }),
  );

  const canManageObligation = authorized(
    permission({
      perm: check("manage", "Obligation"),
    }),
  );

  const canManageOrganization = authorized(
    permission({
      perm: check("manage", "Organization"),
    }),
  );

  const canManagePermission = authorized(
    permission({
      perm: check("manage", "Permission"),
    }),
  );

  const canManagePlaybook = authorized(
    permission({
      perm: check("manage", "Playbook"),
    }),
  );

  const canManageReportTemplate = authorized(
    permission({
      perm: check("manage", "ReportTemplate"),
    }),
  );

  const canManageResponsePlan = authorized(
    permission({
      perm: check("manage", "ResponsePlan"),
    }),
  );

  const canManageRole = authorized(
    permission({
      perm: check("manage", "Role"),
    }),
  );

  const canManageTask = authorized(
    permission({
      perm: check("manage", "Task"),
    }),
  );

  const canManageUser = authorized(
    permission({
      perm: check("manage", "User"),
    }),
  );

  const canAccessAuditSummary = authorized(
    permission({
      perm: check("access", "AuditSummary"),
    }),
  );

  const canReadRegulatoryResearch = authorized(
    permission({
      perm: check("read", "RegulatoryResearch"),
    }),
  );

  const canReadResponsePlan = authorized(
    permission({
      perm: check("read", "ResponsePlan"),
    }),
  );

  const canReadRiskAssessment = authorized(
    permission({
      perm: check("read", "RiskAssessment"),
    }),
  );

  const canReadDashboard = authorized(
    permission({
      perm: check("read", "Dashboard"),
    }),
  );

  const canReadIncidentSummary = authorized(
    permission({
      perm: check("read", "IncidentSummary"),
    }),
  );

  const canReadIncidentAuditLog = authorized(
    permission({
      perm: check("read", "IncidentAuditLog"),
    }),
  );

  const canReadNews = authorized(
    permission({
      perm: check("read", "News"),
    }),
  );

  const canAccessUserHelpGuides = authorized(
    permission({
      perm: check("user", "HelpGuide"),
    }),
  );

  const canAccessAdminHelpGuides = authorized(
    permission({
      perm: check("admin", "HelpGuide"),
    }),
  );

  const canAccessApiHelpGuides = authorized(
    permission({
      perm: check("api", "HelpGuide"),
    }),
  );

  const canAccessIntegrationHelpGuides = authorized(
    permission({
      perm: check("integrations", "HelpGuide"),
    }),
  );

  const canAccessHelpGuides =
    canAccessUserHelpGuides ||
    canAccessAdminHelpGuides ||
    canAccessApiHelpGuides ||
    canAccessIntegrationHelpGuides;

  const authorizedToDeleteMessage = useCallback(
    () =>
      authorized(
        permission({
          perm: check("access", "Message"),
        }),
      ),
    [authorized, permission],
  );

  const authorizedToUpdateMeetingPrivilegedContent = useCallback(
    (action) =>
      authorized(
        permission({
          perm: check("manageMeetingAssets", getIncidentSubject(action?.incident)),
        }),
      ) || action?.userId === user?.id,
    [user, authorized, permission],
  );

  const authorizedToDeleteAction = useCallback(
    (incident) =>
      authorizedAny([
        permission({
          perm: check("delete", getIncidentSubject(incident)),
        }),
        permission({
          perm: check("deleteActions", getIncidentSubject(incident)),
        }),
      ]) || isLeadUser(incident, user),
    [user, authorizedAny, permission],
  );

  const authorizedToDeleteActionDocuments = useCallback(
    (incident) =>
      authorizedAny([
        permission({
          perm: check("delete", getIncidentSubject(incident)),
        }),
        permission({
          perm: check("deleteActions", getIncidentSubject(incident)),
        }),
      ]) || isLeadUser(incident, user),
    [user, authorizedAny, permission],
  );

  const authorizedToDeleteExerciseIncident = useCallback(
    (incident) =>
      authorized(
        permission({
          perm: check("delete", getIncidentSubject(incident)),
        }),
      ) || isLeadUser(incident, user),
    [user, authorized, permission],
  );

  const authorizedToUpdateExerciseIncidentAccess = useCallback(
    (incident) =>
      authorized(
        permission({
          perm: check("manage", getIncidentSubject(incident)),
        }),
      ) || isLeadUserOrHasNone(incident, user),
    [user, authorized, permission],
  );

  const authorizedToUpdateExerciseIncidentDetails = useCallback(
    (incident) =>
      authorized(
        permission({
          perm: check("updateDetails", getIncidentSubject(incident)),
        }),
      ) || isLeadUserOrHasNone(incident, user),
    [user, authorized, permission],
  );

  const authorizedToUpdateActivity = useCallback(
    (action) =>
      authorized(
        permission({
          grant: check("update", "Activity", filterForActivityGrants({action})),
        }),
      ),
    [authorized, permission],
  );

  const authorizedToCreateActivity = useCallback(
    (action) =>
      authorized(
        permission({
          grant: check("create", "Activity", filterForActivityGrants({action})),
        }),
      ),
    [authorized, permission],
  );

  const authorizedToApproveExerciseIncident = useCallback(
    (incident) =>
      authorized(
        permission({
          perm: check("approve", getIncidentSubject(incident)),
        }),
      ) || isLeadUserOrHasNone(incident, user),
    [user, authorized, permission],
  );

  // these currently only contain routes for compliance test instructions
  // and notification center
  const authorizedToRoute = useCallback(
    (path) => {
      const [root] = filter(path.split("/"), Boolean);

      switch (root) {
        case "obligations":
        case "procedures":
          return canManageObligation;
        case "playbooks":
          return canManagePlaybook;
        case "tasks":
          return canManageTask;
        case "organization":
          return canManageOrganization;
        case "users":
          return canManageUser;
        case "data":
          return canManageDataOption;
        case "actions":
          return canAccessExerciseOrIncident;
        case "exercises":
          return canAccessExercise;
        case "workflow":
          return canAccessIncident;
        case "compliance":
          return canReadResponsePlan;
        default:
          return false;
      }
    },
    [
      canManageObligation,
      canManageOrganization,
      canManagePlaybook,
      canManageTask,
      canManageUser,
      canManageDataOption,
      canAccessExercise,
      canAccessExerciseOrIncident,
      canAccessIncident,
      canReadResponsePlan,
    ],
  );

  return {
    permission,
    check,
    authorized,
    authorizedAll,
    authorizedAny,
    authorizedToRoute,
    authorizedToApproveExerciseIncident,
    authorizedToCreateActivity,
    authorizedToDeleteAction,
    authorizedToDeleteActionDocuments,
    authorizedToDeleteExerciseIncident,
    authorizedToDeleteMessage,
    authorizedToUpdateMeetingPrivilegedContent,
    authorizedToUpdateActivity,
    authorizedToUpdateExerciseIncidentAccess,
    authorizedToUpdateExerciseIncidentDetails,
    canAccessAuditSummary,
    canAccessAIUtils,
    canAccessDocumentLibrary,
    canAccessExercise,
    canAccessExerciseAndIncident,
    canAccessExerciseOrIncident,
    canAccessIncident,
    canAccessAdminHelpGuides,
    canAccessApiHelpGuides,
    canAccessUserHelpGuides,
    canAccessIntegrationHelpGuides,
    canAccessHelpGuides,
    canAccessMessage,
    canAccessTrustedAdvisor,
    canShareTrustedAdvisor,
    canAddScenarioPhaseActionsExercise,
    canManageApiKey,
    canManageAuditSummary,
    canManageDataOption,
    canManageDataset,
    canManageDocumentLibrary,
    canManageExerciseMeetingAssets,
    canManageIncidentMeetingAssets,
    canManageIntegration,
    canManageNotification,
    canManageObligation,
    canManageOrganization,
    canManagePlaybook,
    canManagePermission,
    canManageReportTemplate,
    canManageResponsePlan,
    canManageRole,
    canManageTask,
    canManageUser,
    canReadDashboard,
    canReadIncidentSummary,
    canReadIncidentAuditLog,
    canReadNews,
    canReadRegulatoryResearch,
    canReadResponsePlan,
    canReadRiskAssessment,
  };
};
